import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  SpeedDial,
  SpeedDialAction,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import {
  EmojiEventsOutlined,
  CelebrationOutlined,
  SummarizeOutlined,
  Edit,
  Person,
  Comment,
  ForumOutlined,
  AddIcCall,
  CalendarMonth,
  Call,
  VideoCall,
  ArrowBackIosNewRounded,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import UserAvatar from '../../../common/UserAvatar';
import { useFetchProfileQuery, useUploadPhotoMutation } from '../profileApiSlice';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { selectUserStatus } from '../../Auth/authSlice';
import ShabashBadge from '../../../assets/Gifs/medal.gif';
import RnRBadge from '../../../assets/Gifs/trophy.gif';
import GiftBadge from '../../../assets/Gifs/surprise.gif';
import { Can } from '../../../context/Ability';
import ProfileNavbarSkeleton from './ProfileNavbarSkeleton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { variants } from '../../../variants/variants';
import BackButton from '../../../common/BackButton';

const navigations = [
  { value: 'comment', icon: <ForumOutlined />, tooltip: 'Feedback', feature: 'Comment' },
  { value: 'rnr', icon: <EmojiEventsOutlined />, tooltip: 'RnR', feature: 'RnR' },
  { value: 'shabash', icon: <CelebrationOutlined />, tooltip: 'Shabash', feature: 'Shabash' },
  // { value: 'resume', icon: <SummarizeOutlined />, tooltip: 'Resume', feature: 'Resume' },
];
const navigationsProfile = [
  { value: 'chats', icon: <Comment />, feature: 'Chats' },
  // { value: 'profile', icon: <Person />, feature: 'Profile' },
];
const actions = [
  { icon: <CalendarMonth />, name: 'Calendar' },
  { icon: <Call />, name: 'Call' },
  { icon: <VideoCall />, name: 'Video Call' },
];
const ProfileNavbar = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAboutMePage = pathname.split('/').includes('aboutme');
  const path = pathname.split('/')[4];
  const activePath = pathname.split('/')[2];
  const { empCode } = useParams();
  const { data: user, isLoading } = useFetchProfileQuery(empCode);
  const [uploadPhoto] = useUploadPhotoMutation();
  const [open, setOpen] = useState(false);
  const [photoSrc, setPhotoSrc] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [photo, setPhoto] = useState(null);
  const fileInputRef = useRef();
  const userStatus = useSelector((state) => selectUserStatus(state, empCode));
  const [loading, setLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    return () => {
      if (previewSrc) {
        URL.revokeObjectURL(previewSrc);
      }
    };
  }, [previewSrc]);

  useEffect(() => {
    setLoading(true);
  }, [empCode]);

  useEffect(() => {
    if (user !== undefined) {
      setLoading(false);
    }
  }, [user]);

  if (loading || isLoading) {
    return <ProfileNavbarSkeleton />;
  }

  const onInputChange = (e) => {
    const files = e.target.files;
    if (files.length === 0) {
      toast.error('Please select a file before uploading.');
      return;
    }

    const selectedPhoto = files[0];
    setPhoto(selectedPhoto);

    const previewUrl = URL.createObjectURL(selectedPhoto);
    setPreviewSrc(previewUrl);
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.focus();
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();

    let formData = new FormData();
    formData.append('photo', photo);

    try {
      if (!photo) {
        toast.error('Please select a file before uploading.');
        return;
      }
      const response = await uploadPhoto({ empCode, formData });
      if (response.status === 200) {
        toast.success('Photo uploaded successfully!');
      }
      setPhoto(null);
      setPreviewSrc(null);
      resetFileInput();
      setOpen(false);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Error uploading photo. Please try again.');
    }
  };

  const handleClick = (nav) => {
    const close = nav.value === path;
    if (close) {
      navigate(`${activePath}/${empCode}`);
    } else {
      navigate(`${activePath}/${empCode}/${nav.value}`);
    }
    handleCloseMenu();
  };
  const handleActiveClick = (nav) => {
    if (!path) {
      navigate(`${nav.value}/${empCode}`);
    } else {
      navigate(`${nav.value}/${empCode}/${path}`);
    }
  };

  const handleOpenDialog = (src) => {
    setPhotoSrc(src);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  //For mobile button
  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleNavigateBack = () => {
    if (pathname.split('/').length > 4) {
      navigate(`/home/chats/${empCode}`);
    } else {
      navigate('/home/announcement/chatsList');
    }
  };

  return (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        p: 1,
        border: 1,
        borderColor: 'divider',
        background: variants.boxBackground.NAVBAR,
        borderRadius: variants.boxRadius.NAVBAR,
        display: 'flex',
      }}
    >
      {isMobile && <BackButton handleNavigate={handleNavigateBack} />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 0.6 }}>
          {!isAboutMePage ? (
            <>
              <div onClick={() => handleOpenDialog(user && user?.employeePhotoPath)} style={{ cursor: 'default' }}>
                <UserAvatar empCode={empCode} showStatus={true} src={user && user?.employeePhotoPath} size={35} />
              </div>
            </>
          ) : (
            <>
              <Tooltip title="View or edit photo">
                <div onClick={() => handleOpenDialog(user && user?.employeePhotoPath)} style={{ cursor: 'default' }}>
                  <UserAvatar empCode={empCode} showStatus={false} src={user && user?.employeePhotoPath} size={35} />
                </div>
              </Tooltip>
            </>
          )}
          <Box ml={1} sx={{ flex: 1 }}>
            <Typography>{user && user?.firstName+" "+(user?.middleName||"")+" "+user?.lastName}</Typography>
            <Stack direction={'row'} alignItems={'center'} flexWrap={'wrap'}>
              {user?.achievements?.map((achievement) => {
                return (
                  <Tooltip title={achievement?.tooltip}>
                    <IconButton sx={{ p: 0, m: 0 }} size="small">
                      {achievement?.badge === 'RnR' && <img src={RnRBadge} style={{ width: 20, height: 20 }} />}
                    </IconButton>
                  </Tooltip>
                );
              })}
              {user?.achievements?.map((achievement) => {
                return (
                  <Tooltip title={achievement?.tooltip}>
                    <IconButton sx={{ p: 0, m: 0 }} size="small">
                      {achievement?.badge === 'Shabash' && <img src={ShabashBadge} style={{ width: 20, height: 20 }} />}
                    </IconButton>
                  </Tooltip>
                );
              })}
              {user?.achievements?.map((achievement) => {
                return (
                  <Tooltip title={achievement?.tooltip}>
                    <IconButton sx={{ p: 0, m: 0 }} size="small">
                      {achievement?.badge === 'Gift' && <img src={GiftBadge} style={{ width: 20, height: 20 }} />}
                    </IconButton>
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>
          {/* <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <Tooltip title={statusOptions.find(option => option.value === userStatus)?.label || 'Offline'}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: statusOptions.find(option => option.value === userStatus)?.color || 'lightgray',
                  mr: 1,
                }}
              />
            </Tooltip>
            <Typography fontSize={'14px'}>
              {statusOptions.find(option => option.value === userStatus)?.humor || 'Touching some grass'}
            </Typography>
          </Box> */}
        </Box>
        <Can I={'create'} a={'Comment' || 'RnR' || 'Shabash' || 'Resume'}>
          <Box sx={{ display: 'flex', flexDirection: 'row', flex: 0.4, justifyContent: 'flex-end' }}>
            {/* {!isAboutMePage && (
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            {navigationsProfile.map((nav, index) => (
              // <Can key={index} I={'create'} a={nav.feature}>
              <Tooltip title={nav.value} key={nav.value}>
                <IconButton
                  edge="end"
                  onClick={() => handleActiveClick(nav)}
                  sx={{
                    mr: 0.5,
                    // background: nav.value === activePath ? '#0A4FAC' : 'inherit',
                    color: nav.value === activePath ? '#0A4FAC' : 'black',
                    ':hover': {
                      // background: nav.value === activePath ? '#0A4FAC' : 'inherit',
                      // color: nav.value === activePath ? 'white' : 'black',
                    },
                  }}
                >
                  {nav.icon}
                </IconButton>
                </Tooltip>
              // </Can>
            ))}
            <SpeedDial
              ariaLabel='SpeedDial basic example'
              sx={{
                position: 'absolute',
                top: 0,
                // bottom: 720,
                right: 75,
                '& .MuiSpeedDial-fab': {
                  width: 30, // Smaller width
                  height: 30, // Smaller height
                  minHeight: 0, // Override the default minHeight
                  color: 'black',
                  background: 'white',
                  '&:hover': {
                    background: '#EEF7FF',
                  },
                },
                '& .MuiSpeedDialAction-fab': {
                  position: 'static',

                  width: 36, // Smaller action button width
                  height: 36, // Smaller action button height
                  color: 'linear-gradient(180deg, #fff 10%,#EEF5FF 100% )',
                  margin: 1.5,
                  boxShadow: 'rgba(0,0,0,1)',
                },
              }}
              icon={<AddIcCall fontSize='small' />}
              direction='down'
              >
              {actions.map(action => (
                <SpeedDialAction key={action.name} icon={action.icon} tooltipTitle={action.name} />
              ))}
            </SpeedDial>
            </Box>
        )} */}
            <Divider orientation="vertical" variant="middle" flexItem />
            {!isAboutMePage && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    [theme.breakpoints.down('md')]: {
                      display: 'none',
                    },
                  }}
                >
                  {navigations.map((nav, index) => (
                    <Can key={index} I={'create'} a={nav.feature}>
                      <Tooltip title={nav.tooltip} key={nav.value}>
                        <IconButton
                          edge="end"
                          onClick={() => handleClick(nav)}
                          sx={{
                            mx: 1,
                            background: nav.value === path ? '#0A4FAC' : 'inherit',
                            color: nav.value === path ? 'white' : 'black',
                            ':hover': {
                              background: nav.value === path ? '#0A4FAC' : 'inherit',
                              color: nav.value === path ? 'white' : 'black',
                            },
                          }}
                        >
                          {nav.icon}
                        </IconButton>
                      </Tooltip>
                    </Can>
                  ))}
                </Box>
                <Box
                  sx={{
                    display: 'none',
                    [theme.breakpoints.down('md')]: {
                      display: 'flex',
                      // flex: 0,
                    },
                  }}
                >
                  <IconButton onClick={handleMenuClick}>
                    <MoreVertIcon />
                  </IconButton>

                  <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleCloseMenu}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                          width: 32,
                          height: 32,
                          ml: -0.5,
                          mr: 1,
                        },
                        '&:before': {
                          content: '""',
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          right: 14,
                          width: 10,
                          height: 10,
                          bgcolor: 'background.paper',
                          transform: 'translateY(-50%) rotate(45deg)',
                          zIndex: 0,
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    {/* For mobile icon menu */}
                    {navigations.map((nav, index) => (
                      <Can key={index} I={'create'} a={nav.feature}>
                        <MenuItem onClick={() => handleClick(nav)}>
                          <IconButton
                            sx={{ p: 0, mr: 1, fontSize: 18, width: 20, color: nav.value === path ? 'black' : 'black' }}
                          >
                            {nav.icon}
                          </IconButton>
                          <Typography variant="body2">{nav.tooltip}</Typography>
                        </MenuItem>
                      </Can>
                    ))}
                  </Menu>
                </Box>
              </>
            )}
          </Box>
        </Can>
        {isAboutMePage ? (
          <Dialog open={open} onClose={handleCloseDialog} maxWidth="xl" sx={{ backdropFilter: 'blur(3px)' }}>
            <DialogContent
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                <Avatar
                  src={previewSrc || `${process.env.REACT_APP_PUBLIC_UPLOADS_URL}${photoSrc}`}
                  sx={{ height: '20rem', width: '20rem' }}
                />
                <Button
                  fullWidth
                  startIcon={<Edit />}
                  onClick={() => fileInputRef.current?.click()}
                  variant="contained"
                  sx={{ marginTop: 1.5 }}
                >
                  Edit Photo
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={onInputChange}
                />
                <Button fullWidth onClick={handleImageUpload} variant="contained" sx={{ marginTop: 1.5 }} disabled={!photo}>
                  Upload Photo
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        ) : (
          <Dialog
            open={open}
            onClose={handleCloseDialog}
            maxWidth="xl"
            sx={{ backdropFilter: 'blur(3px)' }}
            PaperProps={{
              style: {
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
              },
            }}
          >
            <DialogContent
              sx={{
                borderRadius: '50%',
                overflow: 'hidden',
                padding: 0,
                backgroundColor: 'transparent',
              }}
            >
              <Avatar
                src={`${process.env.REACT_APP_HOST_URL}uploads/${photoSrc}`}
                sx={{ height: '20rem', width: '20rem' }}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
};

export default ProfileNavbar;
